import { motion } from "framer-motion";
import type { Rarity } from "../../lib/omikuji";
import { RARITY_DISPLAY } from "../../lib/omikuji";

type Props = {
  rarity: Rarity;
  /** 表示する文字 (毛筆) */
  body: string;
  /** 開封済みか (closed → opening → opened) */
  state: "closed" | "opening" | "opened";
  /** 並び (left/center/right) */
  position?: "left" | "center" | "right";
  size?: number;
  /** 上部に表示する小ラベル (名前など) */
  ownerLabel?: string;
};

/**
 * 札カード。閉じた状態 → 開封 → 結果表示。
 * レアリティで縁取りと光が変化。
 */
export function OmikujiCard({ rarity, body, state, position = "center", size = 220, ownerLabel }: Props) {
  const rd = RARITY_DISPLAY[rarity];
  const isOpened = state === "opened";

  // レアリティに応じた縁グラデーション
  const borderGradient =
    rarity === "tokudai_kichi"
      ? "linear-gradient(135deg, #ff8a00, #c9a961, #ff8a00, #fffacd, #c9a961)"
      : rarity === "dai_kichi"
        ? "linear-gradient(135deg, #c9a961, #fffacd, #c9a961)"
        : rarity === "chu_kichi"
          ? "linear-gradient(135deg, #c9a961, #8a6b2c)"
          : rarity === "kichi"
            ? "linear-gradient(135deg, #c0c0c0, #8a8a8a)"
            : rarity === "kyo" || rarity === "dai_kyo"
              ? "linear-gradient(135deg, #1a1a1a, #4a4a4a)"
              : "linear-gradient(135deg, #d4c8a8, #a89c7c)";

  return (
    <div
      className="relative flex flex-col items-center"
      style={{
        translate: position === "left" ? "-12% 0" : position === "right" ? "12% 0" : "0 0",
      }}
    >
      {ownerLabel && (
        <p
          className="mb-2 text-xs tracking-[0.3em] text-[var(--color-gold-light)]"
          style={{ fontFamily: "var(--font-fude)" }}
        >
          {ownerLabel}
        </p>
      )}

      <motion.div
        initial={false}
        animate={{
          rotateY: isOpened ? 0 : 180,
          scale: state === "opening" ? 1.06 : 1,
        }}
        transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
        style={{
          width: size,
          height: size * 1.55,
          transformStyle: "preserve-3d",
          perspective: 1000,
        }}
        className="relative"
      >
        {/* 表面 (結果) */}
        <div
          className="absolute inset-0 rounded-md p-[3px]"
          style={{
            background: borderGradient,
            boxShadow: isOpened
              ? `0 0 32px ${rd.glow}, 0 0 64px ${rd.glow}`
              : `0 4px 16px rgba(0,0,0,0.5)`,
            backfaceVisibility: "hidden",
          }}
        >
          <div
            className="w-full h-full rounded-[5px] flex flex-col items-center justify-between py-6 px-4 text-center"
            style={{
              background:
                rarity === "kyo" || rarity === "dai_kyo"
                  ? "linear-gradient(to bottom, #1a1a1a, #2a2a2a)"
                  : "linear-gradient(to bottom, #faf6ee, #f0e8d4)",
              color: rarity === "kyo" || rarity === "dai_kyo" ? "#f5f1e8" : "#1a1a1a",
            }}
          >
            <span
              className="text-3xl"
              style={{
                fontFamily: "var(--font-fude)",
                color: rd.color,
                textShadow: rarity === "tokudai_kichi" ? "0 0 16px #ffd700" : undefined,
              }}
            >
              {rd.ja}
            </span>
            <p
              className="text-sm leading-relaxed px-2"
              style={{ fontFamily: "var(--font-fude)", writingMode: "vertical-rl" }}
            >
              {isOpened ? body : ""}
            </p>
            <span className="text-[9px] tracking-widest opacity-50">{rd.en}</span>
          </div>
        </div>

        {/* 裏面 (閉じた状態) */}
        <div
          className="absolute inset-0 rounded-md p-[3px]"
          style={{
            background: "linear-gradient(135deg, #6b1024, #9f1d35, #6b1024)",
            transform: "rotateY(180deg)",
            backfaceVisibility: "hidden",
            boxShadow: "0 8px 24px rgba(0,0,0,0.7)",
          }}
        >
          <div
            className="w-full h-full rounded-[5px] flex items-center justify-center"
            style={{
              background:
                "repeating-linear-gradient(45deg, #6b1024 0 8px, #7a142a 8px 10px)",
            }}
          >
            <div
              className="w-12 h-12 rounded-full grid place-items-center"
              style={{
                background:
                  "radial-gradient(circle, #fffacd, #c9a961 60%, #8a6b2c)",
                boxShadow: "0 0 16px rgba(201,169,97,0.7)",
              }}
            >
              <span style={{ fontFamily: "var(--font-fude)", fontSize: 24, color: "#6b1024" }}>
                結
              </span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* 大吉以上は光のオーラ */}
      {isOpened && rd.weight >= 3 && rarity !== "kyo" && rarity !== "dai_kyo" && (
        <motion.div
          aria-hidden
          className="absolute inset-0 -z-10 rounded-full"
          initial={{ scale: 0.4, opacity: 0 }}
          animate={{ scale: 1.6, opacity: [0, 0.6, 0] }}
          transition={{ duration: 2.4, repeat: Infinity }}
          style={{
            background: `radial-gradient(circle, ${rd.glow} 0%, transparent 60%)`,
          }}
        />
      )}
    </div>
  );
}
